import Text from '@creator/ui/components/Text/Text';
import { mergeProps } from '@creator/ui/utils/merge-props/merge-props';
import Media, { type MediaProps } from '@src/basic-components/Media/Media';
import MediaPreview from '@src/basic-components/Media/MediaPreview';
import ResponseFullScreenButton from '@src/basic-components/ResponseFullScreenButton/ResponseFullScreenButton';
import { cleanHtmlToPlainText } from '@src/utils/html-utils/html-utils';
import { noop } from '@src/utils/utils';
import type { FC } from 'react';

type AudioToolFile = {
    url: string;
};
type BlockData = {
    url: string;
    withBorder: boolean;
    stretched: boolean;
    file: AudioToolFile;
    caption: string;
    image: { url: string };
};

export interface EditorViewOnlyAudioToolProps {
    postId: string;
    className?: string;
    blockData: BlockData;
    showFullScreenButton?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    mediaProps?: Partial<MediaProps>;
}

const EditorViewOnlyAudioTool: FC<EditorViewOnlyAudioToolProps> = props => {
    const { onClick = noop, blockData, className } = props;

    function renderMedia() {
        const { mediaProps = {} } = props;

        const { url, image } = blockData;

        const _props = mergeProps<[MediaProps, Partial<MediaProps>]>(
            {
                poster: image.url,
                src: url,
                type: 'audio',
            },
            mediaProps
        );

        return <Media {..._props} />;
    }

    function renderCaption() {
        const { caption } = blockData;
        if (!caption) return;
        return (
            <Text enableMultiline={true} className="block mt-1 text-sm text-gray-500" data-placeholder="Caption">
                {cleanHtmlToPlainText(caption)}
            </Text>
        );
    }

    function renderContent() {
        return (
            <div className={'cdx-block audio-tool audio-tool--filled'}>
                <div className="audio-tool__audio">
                    <div className="audio-tool__audio-preloader" />
                    {renderMedia()}
                </div>
                {renderCaption()}
            </div>
        );
    }

    function renderResponseFullScreenButton() {
        const { showFullScreenButton, postId } = props;
        if (!showFullScreenButton) return;

        return <ResponseFullScreenButton className="absolute left-4 top-4 z-1" postId={postId} />;
    }

    return (
        <div onClick={onClick} className={`relative audio-tool ${className}`}>
            {renderContent()}
            {renderResponseFullScreenButton()}
        </div>
    );
};

export default EditorViewOnlyAudioTool;
